/*
  Dawoofie storefront tracker. Loaded by snippets/dawoofie-track.liquid, which puts the page
  context and settings in a JSON script tag (#dw-track-config).

  What it does on every page:
  - keeps a first-party visitor id (_dwid cookie, 400 days) and a session id (30 min gap);
  - reads Shopify's client id (_shopify_y), Meta's _fbp/_fbc (and builds _fbc from fbclid);
  - records the landing URL + referrer of the session, so click ids / UTM reach the server;
  - sends page_viewed, collection_viewed, product_viewed, search_submitted, add_to_cart, checkout_started to the
    collector (sendBeacon, text/plain, no preflight), each with its own event id;
  - fires the matching Meta pixel events from the browser when a pixel id is configured, with
    the same event id, so the warehouse copy can be matched but is never relayed to Meta;
  - stores the visitor id on the cart (attribute _dwid) as a fallback link to the order;
  - runs the ChatGPT ads pixel (page_viewed, contents_viewed, items_added, oppref cart attribute)
    when a ChatGPT pixel id is configured, so there is one tracker and one fetch wrapper;
  - behaviour layer (warehouse only, never relayed to any ad platform): ui_click, variant_selected,
    dialog_open, scroll_depth, rage_click, dead_click, js_error, cart_add_failed, form_submit and a
    page_engagement summary on pagehide. These are queued and sent in batches (5 s / 10 events /
    pagehide) so they add no requests to the critical path and never touch fbq.
*/
(function () {
  'use strict';
  var cfgEl = document.getElementById('dw-track-config');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent || '{}'); } catch (e) { return; }
  if (!cfg.endpoint) return;

  var DAY = 86400;
  var SESSION_GAP_MS = 30 * 60 * 1000;
  var host = location.hostname.replace(/^www\./, '');
  var cookieDomain = /^\d+\.\d+\.\d+\.\d+$|localhost/.test(host) ? '' : '; domain=.' + host;

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var t = Date.now().toString(16);
    return 'dw-' + t + '-' + Math.random().toString(16).slice(2, 14);
  }
  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }
  function writeCookie(name, value, maxAgeSeconds) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + maxAgeSeconds + cookieDomain + '; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
  }
  /* Session state lives in localStorage so a product opened in a new tab stays in the same session. */
  function store(key, value) { try { localStorage.setItem(key, value); } catch (e) {} }
  function load(key) { try { return localStorage.getItem(key) || ''; } catch (e) { return ''; } }

  /* Visitor id */
  var dwid = readCookie('_dwid');
  if (!dwid) { dwid = uuid(); }
  writeCookie('_dwid', dwid, 400 * DAY);

  /* Session: id + landing + referrer, renewed after 30 minutes of silence */
  var now = Date.now();
  var lastSeen = parseInt(load('_dw_last') || '0', 10);
  var sid = load('_dw_sid');
  var params = new URLSearchParams(location.search);
  var hasAdParams = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'oppref', '_kx', 'utm_source'].some(function (k) { return params.has(k); });
  var refHost = '';
  try { refHost = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, '') : ''; } catch (e) {}
  var externalReferrer = !!refHost && refHost !== host && !/(^|\.)myshopify\.com$|^shop\.app$|shopify\.com$/.test(refHost);
  if (!sid || now - lastSeen > SESSION_GAP_MS || hasAdParams || externalReferrer) {
    // A new ad click or outside referrer starts a new touch even inside an active session.
    sid = uuid();
    store('_dw_sid', sid);
    store('_dw_landing', location.href);
    store('_dw_referrer', document.referrer || '');
  }
  store('_dw_last', String(now));
  var landing = load('_dw_landing') || location.href;
  var referrer = load('_dw_referrer');

  /* Meta identifiers. If the pixel has not set _fbc yet, build it from fbclid (Meta's documented format). */
  var fbclid = params.get('fbclid');
  var fbc = readCookie('_fbc');
  if (fbclid && (!fbc || fbc.split('.').slice(3).join('.') !== fbclid)) { fbc = 'fb.1.' + now + '.' + fbclid; writeCookie('_fbc', fbc, 90 * DAY); }
  function fbp() { return readCookie('_fbp'); }
  function shopifyClientId() {
    var y = readCookie('_shopify_y');
    if (y) return y;
    try { return window.ShopifyAnalytics.lib.user().traits().uniqToken || ''; } catch (e) { return ''; }
  }

  /* Transport */
  function send(name, data, eventId) {
    if (!cfg.endpoint) return;
    var body = JSON.stringify({
      source: 'storefront',
      events: [{
        name: name,
        event_id: eventId || uuid(),
        occurred_at: new Date().toISOString(),
        dwid: dwid,
        client_id: shopifyClientId(),
        session_id: sid,
        fbp: fbp() || null,
        fbc: fbc || null,
        url: location.href,
        referrer: document.referrer || null,
        landing: landing,
        data: data || {}
      }]
    });
    var url = cfg.endpoint.replace(/\/$/, '') + '/c';
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) return;
    } catch (e) {}
    try { fetch(url, { method: 'POST', body: body, headers: { 'Content-Type': 'text/plain' }, keepalive: true, mode: 'no-cors' }).catch(function () {}); } catch (e) {}
  }

  /* Batched transport for behaviour events: one beacon per 5 s / 10 events / page hide. */
  var queue = [];
  var flushTimer = null;
  function envelope(name, data, eventId) {
    return {
      name: name, event_id: eventId || uuid(), occurred_at: new Date().toISOString(), dwid: dwid,
      client_id: shopifyClientId(), session_id: sid, fbp: fbp() || null, fbc: fbc || null,
      url: location.href, referrer: document.referrer || null, landing: landing, data: data || {}
    };
  }
  function flush() {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    if (!cfg.endpoint || !queue.length) return;
    var body = JSON.stringify({ source: 'storefront', events: queue.splice(0, 50) });
    var url = cfg.endpoint.replace(/\/$/, '') + '/c';
    try { if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) { if (queue.length) flush(); return; } } catch (e) {}
    try { fetch(url, { method: 'POST', body: body, headers: { 'Content-Type': 'text/plain' }, keepalive: true, mode: 'no-cors' }).catch(function () {}); } catch (e) {}
    if (queue.length) flush();
  }
  function enqueue(name, data) {
    if (!cfg.endpoint) return;
    queue.push(envelope(name, Object.assign({ page_type: (cfg.page || {}).type, handle: (cfg.page || {}).handle }, data || {})));
    if (queue.length >= 10) flush();
    else if (!flushTimer) flushTimer = setTimeout(flush, 5000);
  }
  addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });

  /* Meta pixel (browser side of the 4 funnel events). Only when a pixel id is configured. */
  var meta = null;
  if (cfg.metaPixelId) {
    if (!window.fbq) {
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
        t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      window.fbq('init', cfg.metaPixelId, { external_id: dwid });
    }
    meta = function (name, payload, eventId) { try { window.fbq('track', name, payload, { eventID: eventId }); } catch (e) {} };
  }

  /*
    Meta content id = product-level SKU: the catalog feed lists one item per product with
    g:id = SKU minus the last two segments (colour, size), e.g. DWF-FRE-0003-WT-BLK-S ->
    DWF-FRE-0003-WT. Variant-level ids matched ~1 in 21 events (Meta diagnostic
    pixel_has_low_event_source_match_rate). Falls back to the variant id when there is no SKU.
  */
  function metaContentId(p) {
    var s = String(p.sku || '').trim();
    if (!s) return String(p.variant_id || '');
    var parts = s.split('-');
    return parts.length > 2 ? parts.slice(0, -2).join('-') : s;
  }
  function contentOf(p) {
    return { id: metaContentId(p), quantity: p.quantity || 1, item_price: p.price };
  }
  function metaProduct(p) {
    return {
      content_ids: [metaContentId(p)],
      content_type: 'product',
      content_name: p.title || '',
      content_category: p.breed || '',
      contents: [contentOf(p)],
      value: Number(((p.price || 0) * (p.quantity || 1)).toFixed(2)),
      currency: cfg.currency || 'USD'
    };
  }

  /* Page-level events from the Liquid context */
  var page = cfg.page || {};
  var pvId = uuid();
  send('page_viewed', { page_type: page.type, template: page.template, handle: page.handle }, pvId);
  if (meta) meta('PageView', {}, pvId);

  if (page.type === 'collection' && page.collection) {
    send('collection_viewed', page.collection);
  }
  if (page.type === 'search') {
    send('search_submitted', { terms: params.get('q') || '', results: page.results });
  }
  if (page.type === 'product' && page.product) {
    var p = page.product;
    var vcId = uuid();
    send('product_viewed', p, vcId);
    if (meta) meta('ViewContent', metaProduct(p), vcId);
  }

  /*
    Cart attributes the order carries to the server: _dwid (fallback link to the visitor when the
    checkout pixel is blocked) and oppref (ChatGPT click id: `?oppref=` on the landing URL, kept
    by the SDK in the `__oppref` cookie). One /cart/update.js call, only when something changed.
  */
  var cartAttrs = {};
  if (cfg.cartDwid !== dwid) cartAttrs._dwid = dwid;
  var oppref = (params.get('oppref') || readCookie('__oppref') || '').slice(0, 512);
  if (oppref && oppref !== cfg.cartOppref) cartAttrs.oppref = oppref;
  if (Object.keys(cartAttrs).length) {
    try {
      fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: cartAttrs }), keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  /* Add to cart: every add goes through /cart/add(.js); read the line from that response. */
  function requestedQuantity(body) {
    try {
      if (body instanceof FormData || body instanceof URLSearchParams) return parseInt(body.get('quantity'), 10) || 1;
      if (typeof body === 'string') {
        var d = JSON.parse(body);
        return parseInt((d.items && d.items[0] && d.items[0].quantity) || d.quantity, 10) || 1;
      }
    } catch (e) {}
    return 1;
  }
  function reportAdded(payload, quantity) {
    var lines = Array.isArray(payload.items) ? payload.items : [payload];
    lines.forEach(function (line) {
      if (!line || line.price == null) return;
      var unit = (line.final_price != null ? line.final_price : line.price) / 100;
      var item = {
        sku: line.sku || '', variant_id: line.variant_id || line.id, product_id: line.product_id,
        handle: line.handle, title: line.product_title || line.title, variant_title: line.variant_title,
        price: unit, quantity: lines.length === 1 ? quantity : (line.quantity || 1),
        breed: breedFromProperties(line), line: line.product_type || ''
      };
      cartAdd(item);
      var id = uuid();
      send('add_to_cart', item, id);
      if (meta) meta('AddToCart', metaProduct(item), id);
    });
  }
  function breedFromProperties(line) {
    // The product page knows its breed; for adds from other pages derive it from the SKU
    // (DWF-{BREED}-{NNNN}-...) using the code map the snippet passes in.
    if (page.product && page.product.sku === line.sku) return page.product.breed;
    var m = /^DWF-([A-Z]{3})-\d{4}-/.exec(line.sku || '');
    return (m && cfg.breedCodes && cfg.breedCodes[m[1]]) || '';
  }
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var result = nativeFetch.apply(this, arguments);
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/cart(\.js)?(\?|$)|\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url)) {
        result.then(function (response) { if (response.ok) return response.clone().json().then(cartFromJson); }).catch(function () {});
      }
      if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
        var quantity = requestedQuantity(init && init.body);
        result.then(function (response) {
          if (!response.ok) {
            return response.clone().text().then(function (t) { enqueue('cart_add_failed', { status: response.status, message: String(t || '').slice(0, 160) }); }).catch(function () {});
          }
          return response.clone().json().then(function (payload) { reportAdded(payload, quantity); });
        }).catch(function () {});
      }
    } catch (e) {}
    return result;
  };

  /*
    Checkout start. A buyer who pays with Shop Pay leaves for shop.app the moment Check out is
    pressed and no Shopify customer event fires there (only checkout_completed, back on the
    thank-you page), so InitiateCheckout is sent from the press itself, with the cart as the
    page knew it. The event id is kept in the `_dwic` cookie: when the checkout pixel does see
    checkout_started (card checkout) it reuses the id, so Meta and the warehouse count one start.
  */
  var cart = cfg.cart && Array.isArray(cfg.cart.items) ? cfg.cart : { total: 0, items: [] };
  function cartLine(l) {
    return { sku: l.sku || '', variant_id: l.variant_id || l.id, product_id: l.product_id, title: l.product_title || l.title || '',
      line: l.product_type || '', price: (l.final_price != null ? l.final_price : l.price) / 100, quantity: l.quantity || 1 };
  }
  function cartFromJson(c) {
    if (!c || !Array.isArray(c.items)) return;
    cart = { total: (c.total_price || 0) / 100, items: c.items.map(cartLine) };
  }
  function cartAdd(item) {
    var found = cart.items.filter(function (l) { return String(l.variant_id) === String(item.variant_id); })[0];
    if (found) found.quantity += item.quantity; else cart.items.push(item);
    cart.total = cart.items.reduce(function (sum, l) { return sum + (l.price || 0) * (l.quantity || 1); }, 0);
  }
  var lastCheckoutAt = 0;
  function startCheckout() {
    var at = Date.now();
    if (at - lastCheckoutAt < 2000) return; // click and submit of the same press
    lastCheckoutAt = at;
    var sig = cart.items.map(function (l) { return l.variant_id + ':' + l.quantity; }).sort().join(',');
    var prev = {};
    try { prev = JSON.parse(load('_dw_ic') || '{}'); } catch (e) {}
    // Pressing Check out again for the same cart within 30 min is the same start: same id, counted once.
    var id = prev.sig === sig && at - (prev.t || 0) < SESSION_GAP_MS && prev.id ? prev.id : uuid();
    store('_dw_ic', JSON.stringify({ id: id, sig: sig, t: at }));
    writeCookie('_dwic', id, 30 * 60);
    var currency = cfg.currency || 'USD';
    var numItems = cart.items.reduce(function (n, l) { return n + (l.quantity || 1); }, 0);
    send('checkout_started', { total: cart.total, currency: currency, num_items: numItems, lines: cart.items }, id);
    if (meta) meta('InitiateCheckout', {
      value: Number(cart.total.toFixed(2)), currency: currency, num_items: numItems, content_type: 'product',
      content_ids: cart.items.map(metaContentId), contents: cart.items.map(contentOf)
    }, id);
  }
  var CHECKOUT_CONTROLS = 'button[name="checkout"],input[name="checkout"],a[href^="/checkout"],.shopify-payment-button__button,shop-pay-button,[data-shopify="dynamic-checkout-cart"] button';
  document.addEventListener('submit', function (ev) {
    var f = ev.target, s = ev.submitter;
    if ((s && s.name === 'checkout') || (f && f.getAttribute && /\/checkout(\?|$)/.test(f.getAttribute('action') || ''))) startCheckout();
  }, true);
  document.addEventListener('click', function (ev) {
    var el = ev.target && ev.target.closest ? ev.target.closest(CHECKOUT_CONTROLS) : null;
    if (el) startCheckout();
  }, true);

  /* ------------------------------------------------------------------------------------------
     Behaviour layer. Everything below only feeds the warehouse. Attached after load so it never
     competes with rendering or with the pixels above.
     ------------------------------------------------------------------------------------------ */
  function startBehaviour() {
    var t0 = performance.now();
    var clicks = 0, rage = 0, dead = 0, errors = 0, firstInteraction = null, maxScroll = 0;
    var visibleMs = 0, visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
    var INTERACTIVE = 'a,button,input,select,textarea,label,summary,[role="button"],[role="tab"],[role="option"],[tabindex],dialog';

    function text(el) {
      if (!el) return '';
      var t = el.getAttribute('aria-label') || el.getAttribute('title') || (el.matches('input,select,textarea') ? (el.getAttribute('placeholder') || el.name || '') : (el.innerText || el.textContent || ''));
      return String(t).replace(/\s+/g, ' ').trim().slice(0, 60);
    }
    function dwAttrs(el) {
      var out = [];
      for (var i = 0; i < el.attributes.length; i++) { var n = el.attributes[i].name; if (n.indexOf('data-dw') === 0) out.push(n.slice(5) + (el.attributes[i].value ? '=' + String(el.attributes[i].value).slice(0, 30) : '')); }
      return out;
    }
    function dwClass(el) {
      var cls = (el.className && typeof el.className === 'string' ? el.className : '').split(/\s+/).filter(Boolean);
      for (var i = 0; i < cls.length; i++) if (cls[i].indexOf('dw-') === 0) return cls[i];
      return cls[0] || '';
    }
    function describe(el) {
      var target = el.closest ? (el.closest(INTERACTIVE) || el) : el;
      var section = target.closest ? target.closest('[id^="shopify-section-"],[data-section-type]') : null;
      var dialog = target.closest ? target.closest('dialog') : null;
      var d = {
        tag: target.tagName.toLowerCase(),
        text: target.matches('input[type="email"],input[type="text"],input[type="tel"],textarea') ? '' : text(target),
        id: target.id || '', cls: dwClass(target), dw: dwAttrs(target),
        type: target.getAttribute('type') || '', role: target.getAttribute('role') || ''
      };
      if (target.tagName === 'A' && target.getAttribute('href')) { try { d.href = new URL(target.getAttribute('href'), location.href).pathname.slice(0, 120); } catch (e) {} }
      if (section) d.section = (section.id || '').replace('shopify-section-', '').slice(0, 60) || section.getAttribute('data-section-type');
      if (dialog) d.dialog = dialog.id || dwClass(dialog);
      d.key = d.tag + (d.dw.length ? '[' + d.dw[0].split('=')[0] + ']' : d.cls ? '.' + d.cls : d.id ? '#' + d.id : '') + (d.text ? ':' + d.text.slice(0, 30) : d.href ? ':' + d.href.slice(0, 30) : '');
      return d;
    }

    /* Clicks, rage clicks (3 in 1 s within 30 px), dead clicks (nothing changed within 800 ms). */
    var recent = [];
    var mutations = 0;
    var mo = new MutationObserver(function () { mutations++; });
    try { mo.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'class', 'hidden', 'aria-expanded', 'aria-selected', 'checked', 'src'] }); } catch (e) {}
    document.addEventListener('click', function (ev) {
      var el = ev.target && ev.target.nodeType === 1 ? ev.target : (ev.target && ev.target.parentElement);
      if (!el) return;
      clicks++;
      if (firstInteraction === null) firstInteraction = Math.round(performance.now() - t0);
      var now = Date.now();
      recent.push({ t: now, x: ev.clientX, y: ev.clientY });
      recent = recent.filter(function (r) { return now - r.t <= 1000; });
      var near = recent.filter(function (r) { return Math.abs(r.x - ev.clientX) <= 30 && Math.abs(r.y - ev.clientY) <= 30; });
      var d = describe(el);
      var interactive = !!(el.closest && el.closest(INTERACTIVE));
      if (interactive && clicks <= 80) enqueue('ui_click', d);
      if (near.length >= 3) { rage++; recent = []; enqueue('rage_click', d); }
      /* Submit buttons and links navigate after 800 ms on slow networks: never count them as dead. */
      var navigates = (d.tag === 'a' && d.href) || d.type === 'submit' || (el.closest && el.closest('form') && el.closest('button,input[type="submit"]'))
        || /^(input|select|textarea|label)$/.test(d.tag);   // focusing a field changes nothing in the DOM either
      var m0 = mutations, href0 = location.href;
      setTimeout(function () {
        if (!navigates && mutations === m0 && location.href === href0) { dead++; enqueue('dead_click', d); }
      }, 800);
    }, { capture: true, passive: true });

    /* Variant / option selection (Horizon radios and selects named after the option). */
    document.addEventListener('change', function (ev) {
      var el = ev.target;
      if (!el || !el.matches || !el.matches('input[type="radio"],select')) return;
      if (firstInteraction === null) firstInteraction = Math.round(performance.now() - t0);
      var option = (el.closest('[data-option-name]') || {}).getAttribute ? el.closest('[data-option-name]').getAttribute('data-option-name') : '';
      option = option || (el.name || '').replace(/^options\[(.*)\]$/, '$1').replace(/-\d+.*$/, '').replace(/-swatch$/, '').slice(0, 30);
      var value = String(el.value || '').slice(0, 40);
      if (option && value) enqueue('variant_selected', { option: option, value: value, section: (describe(el).section || '') });
    }, { capture: true, passive: true });

    /* Dialogs opening (size helper, cart drawer, quick add, popup...). */
    try {
      new MutationObserver(function (list) {
        list.forEach(function (m) {
          var el = m.target;
          if (m.attributeName === 'open' && el.tagName === 'DIALOG' && el.hasAttribute('open')) {
            var head = el.querySelector('h1,h2,h3,[id$="-title"]');
            enqueue('dialog_open', { id: el.id || '', cls: dwClass(el), title: head ? text(head) : '' });
          }
        });
      }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
    } catch (e) {}

    /* Scroll depth milestones. */
    /* Throttled with setTimeout, not requestAnimationFrame: rAF never fires in background tabs. */
    var marks = [25, 50, 75, 100], ticking = false;
    function onScroll() {
      if (ticking) return; ticking = true;
      setTimeout(function () {
        ticking = false;
        var h = document.documentElement.scrollHeight - innerHeight;
        var pct = h > 0 ? Math.min(100, Math.round(((scrollY + innerHeight) / document.documentElement.scrollHeight) * 100)) : 100;
        if (pct > maxScroll) maxScroll = pct;
        while (marks.length && pct >= marks[0]) enqueue('scroll_depth', { pct: marks.shift() });
      }, 250);
    }
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* Form submits (no values) and JS errors (capped). */
    document.addEventListener('submit', function (ev) {
      var f = ev.target; if (!f || f.tagName !== 'FORM') return;
      var d = { id: f.id || '', cls: dwClass(f), dw: dwAttrs(f), action: '' };
      try { d.action = new URL(f.getAttribute('action') || location.href, location.href).pathname.slice(0, 80); } catch (e) {}
      enqueue('form_submit', d);
    }, { capture: true, passive: true });
    addEventListener('error', function (ev) {
      if (errors++ >= 5) return;
      var src = String(ev.filename || '').split('/').pop().split('?')[0];
      enqueue('js_error', { message: String(ev.message || '').slice(0, 150), source: src.slice(0, 80), line: ev.lineno || 0 });
    });
    addEventListener('unhandledrejection', function (ev) {
      if (errors++ >= 5) return;
      var r = ev.reason; enqueue('js_error', { message: String(r && (r.message || r)).slice(0, 150), source: 'promise', line: 0 });
    });

    /* Page engagement summary when the page is left. */
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') visibleSince = Date.now();
      else if (visibleSince) { visibleMs += Date.now() - visibleSince; visibleSince = null; }
    });
    var sent = false;
    function engagement() {
      if (sent) return; sent = true;
      if (visibleSince) { visibleMs += Date.now() - visibleSince; visibleSince = null; }
      enqueue('page_engagement', { visible_ms: visibleMs, max_scroll: maxScroll, first_interaction_ms: firstInteraction, clicks: clicks, rage_clicks: rage, dead_clicks: dead, js_errors: errors });
      flush();
    }
    addEventListener('pagehide', engagement);
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') engagement(); });
  }
  /*
    Microsoft Clarity (session replay + heatmaps), only when a project id is configured. Loaded
    after the page is complete so it never competes with LCP. The visitor id and session id are
    attached so a warehouse session can be opened in Clarity (filter: custom tag "dwid").
  */
  function startClarity() {
    if (!cfg.clarityId || window.clarity) return;
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', cfg.clarityId);
    try {
      window.clarity('identify', dwid, sid);
      window.clarity('set', 'dwid', dwid);
      window.clarity('set', 'session', sid);
      if (page.type) window.clarity('set', 'page_type', String(page.type));
      if (page.product && page.product.breed) window.clarity('set', 'breed', String(page.product.breed));
    } catch (e) {}
  }
  /* Real-user load timing (field data): sent once per page right after load, LCP refined at pagehide. */
  function startTiming() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (!nav) return;
      var paint = {};
      performance.getEntriesByType('paint').forEach(function (p) { paint[p.name] = Math.round(p.startTime); });
      var conn = navigator.connection || {};
      var lcp = null;
      try {
        new PerformanceObserver(function (list) { var e = list.getEntries(); if (e.length) lcp = Math.round(e[e.length - 1].startTime); })
          .observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (e) {}
      var sentTiming = false;
      function sendTiming() {
        if (sentTiming) return; sentTiming = true;
        enqueue('page_timing', {
          ttfb_ms: Math.round(nav.responseStart), dom_interactive_ms: Math.round(nav.domInteractive),
          dcl_ms: Math.round(nav.domContentLoadedEventEnd), load_ms: Math.round(nav.loadEventEnd || performance.now()),
          fcp_ms: paint['first-contentful-paint'] || null, lcp_ms: lcp, html_kb: Math.round((nav.transferSize || 0) / 1024),
          resources: performance.getEntriesByType('resource').length,
          conn_type: conn.effectiveType || null, downlink_mbps: conn.downlink || null, rtt_ms: conn.rtt || null, save_data: !!conn.saveData,
          device_memory_gb: navigator.deviceMemory || null, cores: navigator.hardwareConcurrency || null
        });
      }
      setTimeout(sendTiming, 4000);
      addEventListener('pagehide', sendTiming);
    } catch (e) {}
  }
  /* Clarity is the heaviest third-party script: start it on the first interaction, or 4 s after load. */
  function startClarityLazily() {
    var started = false;
    function go() { if (started) return; started = true; startClarity(); }
    ['pointerdown', 'touchstart', 'keydown', 'scroll'].forEach(function (ev) { addEventListener(ev, go, { once: true, passive: true }); });
    setTimeout(go, 4000);
  }
  function startDeferred() { startBehaviour(); startClarityLazily(); startTiming(); }
  if (document.readyState === 'complete') setTimeout(startDeferred, 0);
  else addEventListener('load', function () { setTimeout(startDeferred, 0); });
})();
